// Forskrift om tryggere bolighandel §2-22
// Informasjon om årsak og konsekvens ved tilstandsgrad 2 og 3

export const paragraf222Info = {
  tittel: 'Forskrift §2-22: Årsak og konsekvens ved TG2 og TG3',

  forklaring: `
Når en bygningsdel eller et rom får tilstandsgrad 2 (TG2) eller tilstandsgrad 3 (TG3),
skal tilstandsrapporten inneholde opplysninger om:

1. **ÅRSAK** - Hva som har forårsaket tilstanden
2. **KONSEKVENS** - Hvilke følger tilstanden har eller kan få

Dette er OBLIGATORISK etter forskriften §2-22.
`,

  eksempler: {
    TG2: {
      beskrivelse: 'God - men vedlikeholdsetterslep',
      eksempel_aarsak: [
        'Normal slitasje over tid',
        'Manglende vedlikehold',
        'Utilstrekkelig ventilasjon',
        'Normal aldring av materialer',
      ],
      eksempel_konsekvens: [
        'Vedlikehold må utføres innen 2-5 år',
        'Økende vedlikeholdskostnader hvis utsatt',
        'Kan utvikle seg til TG3 hvis ikke utbedret',
        'Redusert levetid for bygningsdelen',
      ],
    },

    TG3: {
      beskrivelse: 'Dårlig - tiltak nødvendig',
      eksempel_aarsak: [
        'Vannskade/fuktskade',
        'Membran defekt eller mangler',
        'Manglende ventilasjon over lang tid',
        'Konstruksjonsfeil',
        'Skadedyr (råte, sopp)',
        'Alvorlig slitasje',
      ],
      eksempel_konsekvens: [
        'Utbedring må utføres umiddelbart',
        'Risiko for videre skadeutvikling',
        'Helsefare (mugg, sopp)',
        'Betydelige utbedringskostnader',
        'Kan påvirke andre bygningsdeler',
        'Redusert sikkerhet/funksjon',
      ],
    },
  },

  mal_tekst: {
    TG2: {
      aarsak_mal: 'Årsak: [Beskriv hva som har forårsaket tilstanden, f.eks. "Normal slitasje over 15 år, kombinert med manglende vedlikehold av fuger"]',
      konsekvens_mal: 'Konsekvens: [Beskriv konsekvenser, f.eks. "Vedlikehold må utføres innen 2-3 år for å unngå økte kostnader og overgang til TG3"]',
    },
    TG3: {
      aarsak_mal: 'Årsak: [Beskriv årsak detaljert, f.eks. "Defekt membran har ført til vanninntrenging i veggkonstruksjonen over tid"]',
      konsekvens_mal: 'Konsekvens: [Beskriv alvorlighetsgrad, f.eks. "Umiddelbar utbedring nødvendig. Risiko for videre råteskader og helsefare grunnet mugg. Estimert kostnad: 80.000-120.000 kr"]',
    },
  },

  krav: [
    'Årsak og konsekvens MÅ dokumenteres for TG2 og TG3',
    'Beskrivelsen skal være konkret og forståelig',
    'Ved TG3: Angi estimert kostnad hvis mulig',
    'Ved TG3: Angi hastegrad (umiddelbart/snarest)',
  ],
};

export const validerTGBeskrivelse = (tilstandsgrad, aarsak, konsekvens) => {
  if (tilstandsgrad === 'TG0' || tilstandsgrad === 'TG1') {
    return { gyldig: true };
  }

  if (tilstandsgrad === 'TG2' || tilstandsgrad === 'TG3') {
    const manglerAarsak = !aarsak || aarsak.trim().length < 10;
    const manglerKonsekvens = !konsekvens || konsekvens.trim().length < 10;

    if (manglerAarsak || manglerKonsekvens) {
      return {
        gyldig: false,
        feil: 'Forskrift §2-22 krever beskrivelse av årsak og konsekvens ved TG2/TG3',
      };
    }
  }

  return { gyldig: true };
};

// Intelligent validering av årsak
export const validerAarsak = (aarsak, tilstandsgrad) => {
  if (!aarsak || aarsak.trim().length < 10) {
    return {
      gyldig: false,
      nivaa: 'feil',
      melding: 'Årsak må være minst 10 tegn',
    };
  }

  const tekst = aarsak.toLowerCase().trim();
  const ordtelling = tekst.split(/\s+/).length;

  // Sjekk for typiske malsetninger/plassholdere
  const malSetninger = [
    'beskriv hva som har',
    'skriv årsak her',
    'årsak:',
    '[',
    'f.eks',
    'eksempel',
  ];

  for (const mal of malSetninger) {
    if (tekst.includes(mal)) {
      return {
        gyldig: false,
        nivaa: 'advarsel',
        melding: 'Ser ut som du bruker mal-tekst. Vennligst beskriv konkret årsak.',
      };
    }
  }

  // KRITISK: Sjekk for positive beskrivelser som ikke kan være årsak til TG2/TG3
  // Dette MÅ sjekkes FØR vi sjekker om teksten har årsaksord
  if (tilstandsgrad === 'TG2' || tilstandsgrad === 'TG3') {
    const positiveOrd = [
      'meget god', 'meget bra', // sjekk sammensatte først
      'god', 'gode', 'bra', 'flott', 'fin', 'fine', 'pent', 'pen',
      'nytt', 'ny', 'nye', 'perfekt', 'utmerket',
      'ingen tegn', 'ingen skade', 'ingen feil', 'ingen problem',
    ];

    // Sjekk om teksten primært beskriver positive forhold
    const harPositivOrd = positiveOrd.some(ord => tekst.includes(ord));

    // Sjekk også at det IKKE finnes problembeskrivelser
    const problemOrd = [
      'men', 'dog', 'imidlertid', 'likevel', 'bortsett fra',
      'skade', 'feil', 'defekt', 'slitasje', 'mangl',
    ];
    const harProblemOrd = problemOrd.some(ord => tekst.includes(ord));

    if (harPositivOrd && !harProblemOrd) {
      return {
        gyldig: false,
        nivaa: 'feil',
        melding: `Årsaken beskriver positive forhold. TG${tilstandsgrad === 'TG2' ? '2' : '3'} indikerer problem - beskriv HVA som er galt (f.eks. slitasje, skade, manglende vedlikehold).`,
      };
    }
  }

  // Sjekk om teksten er for generisk
  if (ordtelling < 5) {
    return {
      gyldig: false,
      nivaa: 'advarsel',
      melding: 'Beskrivelsen er for kort. Vær mer spesifikk om hva som har forårsaket tilstanden.',
    };
  }

  // Sjekk etter konkrete årsaksord
  const aarsakOrd = [
    'slitasje', 'skade', 'fukt', 'vann', 'lekkasje', 'råte', 'sopp', 'mugg',
    'membran', 'ventilasjon', 'vedlikehold', 'aldring', 'konstruksjon',
    'installasjon', 'feil', 'defekt', 'mangler', 'mangl', 'sprekk', 'deformering',
    'utslitt', 'redusert', 'svekket', 'dårlig', 'utilstrekkelig',
  ];

  const harAarsakOrd = aarsakOrd.some(ord => tekst.includes(ord));

  if (!harAarsakOrd && ordtelling < 8) {
    return {
      gyldig: false,
      nivaa: 'advarsel',
      melding: 'Mangler konkret årsak. Beskriv hva som har forårsaket tilstanden (f.eks. fuktskade, slitasje, manglende vedlikehold)',
    };
  }

  // Sjekk for TG3-spesifikke krav
  if (tilstandsgrad === 'TG3') {
    const alvorligeAarsaker = [
      'fukt', 'vann', 'lekkasje', 'råte', 'sopp', 'mugg', 'membran',
      'konstruksjon', 'skade', 'defekt',
    ];
    const harAlvorligAarsak = alvorligeAarsaker.some(ord => tekst.includes(ord));

    if (!harAlvorligAarsak) {
      return {
        gyldig: true,
        nivaa: 'advarsel',
        melding: 'TG3 indikerer alvorlig tilstand. Sørg for å beskrive den underliggende årsaken (f.eks. fuktskade, råte, konstruksjonsfeil)',
      };
    }
  }

  return {
    gyldig: true,
    nivaa: 'ok',
    melding: 'Beskrivelsen ser god ut',
  };
};

// Intelligent validering av konsekvens
export const validerKonsekvens = (konsekvens, tilstandsgrad) => {
  if (!konsekvens || konsekvens.trim().length < 10) {
    return {
      gyldig: false,
      nivaa: 'feil',
      melding: 'Konsekvens må være minst 10 tegn',
    };
  }

  const tekst = konsekvens.toLowerCase().trim();
  const ordtelling = tekst.split(/\s+/).length;

  // Sjekk for typiske malsetninger
  const malSetninger = [
    'beskriv konsekvens',
    'skriv konsekvens her',
    'konsekvens:',
    '[',
    'f.eks',
    'eksempel',
  ];

  for (const mal of malSetninger) {
    if (tekst.includes(mal)) {
      return {
        gyldig: false,
        nivaa: 'advarsel',
        melding: 'Ser ut som du bruker mal-tekst. Vennligst beskriv konkret konsekvens.',
      };
    }
  }

  if (ordtelling < 5) {
    return {
      gyldig: false,
      nivaa: 'advarsel',
      melding: 'Beskrivelsen er for kort. Beskriv tydelig hvilke følger tilstanden har eller kan få.',
    };
  }

  // Sjekk for konkrete konsekvensord
  const konsekvensOrd = [
    'utbedring', 'kostnad', 'vedlikehold', 'utføres', 'nødvendig', 'risiko',
    'skade', 'påvirke', 'helse', 'sikkerhet', 'funksjon', 'år', 'umiddelbart',
    'snarest', 'kr', 'kroner', 'estimert', 'levetid',
  ];

  const harKonsekvensOrd = konsekvensOrd.some(ord => tekst.includes(ord));

  if (!harKonsekvensOrd && ordtelling < 8) {
    return {
      gyldig: true,
      nivaa: 'tips',
      melding: 'Tips: Beskriv konkret hva konsekvensen er (f.eks. tidsperspektiv, kostnader, sikkerhet)',
    };
  }

  // TG2-spesifikke krav
  if (tilstandsgrad === 'TG2') {
    const harTidsperspektiv = /\d+.*år|innen|snart|vedlikehold/.test(tekst);

    if (!harTidsperspektiv) {
      return {
        gyldig: true,
        nivaa: 'tips',
        melding: 'Tips: For TG2, beskriv når vedlikehold bør utføres (f.eks. "innen 2-3 år")',
      };
    }
  }

  // TG3-spesifikke krav
  if (tilstandsgrad === 'TG3') {
    const harHastegrad = /umiddelbart|snarest|straks|nødvendig|akutt/.test(tekst);
    const harAlvorlighet = /risiko|fare|skade|alvorlig|betydelig/.test(tekst);
    const harKostnad = /kr|kroner|\d+\s*000|estimert|kostnad|pris/.test(tekst);

    if (!harHastegrad) {
      return {
        gyldig: true,
        nivaa: 'advarsel',
        melding: 'TG3 krever hastegrad: Beskriv når utbedring må utføres (f.eks. "umiddelbart", "snarest")',
      };
    }

    if (!harKostnad) {
      return {
        gyldig: true,
        nivaa: 'advarsel',
        melding: 'TG3 krever kostnadsestimat: Legg til estimert kostnad for utbedring (f.eks. "Estimert kostnad: 50.000-80.000 kr")',
      };
    }

    if (!harAlvorlighet && ordtelling < 12) {
      return {
        gyldig: true,
        nivaa: 'tips',
        melding: 'Tips: Beskriv alvorlighetsgraden for å gi kjøper full forståelse',
      };
    }
  }

  return {
    gyldig: true,
    nivaa: 'ok',
    melding: 'Beskrivelsen ser god ut',
  };
};
